/**
 * mfaGate — "must this signed-in user set up two-factor before using the app?"
 *
 * The backend answers through /auth/mfa/status (enrollmentRequired: the tenant
 * policy requires MFA, the user has no authenticator, and the current session
 * did not satisfy MFA another way). The answer is mirrored into Redux so
 * AuthenticatedRoute can redirect to /auth/mfa/enroll on every protected route
 * until enrolment completes. Login already forces enrolment for new sign-ins;
 * this covers sessions that were open when an admin turned the policy on, and
 * sign-in paths that mint a session directly (invite acceptance).
 */
import { mfaStatus } from '../api_service/mfa_api';
import { setMfaEnrollmentRequired } from '../redux_store/features/auth_gate_reducer';
import { store } from '../redux_store/store';
import { onLogout } from './session';

export const MFA_ENROLL_PATH = '/auth/mfa/enroll';

/** Build the enrolment URL that returns the user to `next` afterwards. */
export function mfaEnrollPath(next?: string): string {
  return next && next !== '/' ? `${MFA_ENROLL_PATH}?next=${encodeURIComponent(next)}` : MFA_ENROLL_PATH;
}

/** Ask the backend and update the gate. Resolves to whether enrolment is required.
 *  Every outcome (including a failed call) is written to the store, so a stale
 *  `true` from an earlier user or a transient error never lingers; on failure
 *  the backend refresh backstop still applies. */
export async function checkMfaGate(): Promise<boolean> {
  let required = false;
  try {
    const res = await mfaStatus();
    if (res.ok) {
      const data = await res.json();
      required = data?.enrollmentRequired === true;
    }
  } catch {
    required = false;
  }
  store.dispatch(setMfaEnrollmentRequired(required));
  return required;
}

export function clearMfaGate(): void {
  store.dispatch(setMfaEnrollmentRequired(false));
}

// Sign-out resets the gate: the next user in this tab starts clean.
onLogout(clearMfaGate);
